// admin/src/pages/settings/Settings.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Container, Button, Spinner as BSpinner, Alert } from "react-bootstrap";
import { Colors } from "../../constants/Colors";
import { fetchFareSettings, updateFareSetting } from "../../services/fareApi";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

/* ================================================================== */
/* CONSTANTS                                                          */
/* ================================================================== */

const MySwal = withReactContent(Swal);

const T = {
  radius: 14,
  radiusSm: 10,
  shadow: "0 1px 2px rgba(16,24,40,.04), 0 4px 16px rgba(16,24,40,.06)",
  shadowHover: "0 4px 8px rgba(16,24,40,.06), 0 12px 28px rgba(16,24,40,.10)",
  border: `1px solid ${Colors.BORDER_GRAY}`,
  font: "'Inter', 'Segoe UI', system-ui, sans-serif",
};

const ERROR_COLOR = Colors.ERROR || "#EF4444";
const TEXT_MEDIUM = Colors.TEXT_MEDIUM || Colors.TEXT_DARK;

const TABS = [
  { key: "Fare Configuration", icon: "bi-truck" },
  { key: "Funeral Rates", icon: "bi-heart" },
  { key: "Wedding Rates", icon: "bi-calendar-event" },
  { key: "School Run Rates", icon: "bi-mortarboard" },
  { key: "Service Limits", icon: "bi-shield-check" },
  { key: "Cancellation Policy", icon: "bi-x-octagon" },
];

const SERVICE_CONFIGS = {
  charter: {
    empty: {
      base_fee: "",
      minimum_fare: "",
      diesel_price: "",
      fuel_efficiency_kmpl: "",
      fuel_buffer_percent: "",
      driver_hourly_rate: "",
      driver_meal_allowance: 200,
      minimum_billable_hours: "",
      driver_daily_rate: "",
      vehicle_cost_per_km: "",
      profit_margin_percent: "",
      vehicle_standby_rate: 1200,
      pickup_surcharge_free_km: 5,
      pickup_surcharge_per_km: 15,
      seasonal_multipliers: "{}",
      road_condition: {
        normal: 1.0,
        hilly: 1.2,
        difficult: 1.4,
        mountainous_municipalities: ["Buhi", "Bato"],
        malinao_range_longitude: 123.55,
      },
    },
  },
  funeral: {
    empty: {
      base_service: "",
      fuel_allowance: "",
      driver_wage: "",
      target: "",
    },
  },
  wedding: {
    empty: {
      zones: {
        1: { base: "", fuel: "", driver: "", reservation: "", target: "" },
        2: { base: "", fuel: "", driver: "", reservation: "", target: "" },
        3: { base: "", fuel: "", driver: "", reservation: "", target: "" },
      },
    },
  },
  school_run: {
    empty: {
      in_district: {
        base: "",
        driver: "",
        fuel: "",
        reservation: "",
        target: "",
      },
      out_district: {
        base: "",
        driver: "",
        fuel: "",
        reservation: "",
        target: "",
      },
      overage_threshold_km: "",
      overage_per_km: "",
    },
  },
};

const WEDDING_ZONE_LABELS = {
  1: "Zone 1 — Same municipality (as operator)",
  2: "Zone 2 — Different Rinconada municipality",
  3: "Zone 3 — Outside Rinconada",
};

const parse = (v, fallback = 0) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : fallback;
};

const safeStringify = (v, indent = 2) => {
  try {
    return JSON.stringify(v, null, indent);
  } catch {
    return "{}";
  }
};

const showSuccessAlert = (title, text) =>
  MySwal.fire({
    icon: "success",
    title,
    text,
    confirmButtonColor: Colors.PRIMARY,
  });

const showErrorAlert = (title, text) =>
  MySwal.fire({
    icon: "error",
    title,
    text,
    confirmButtonColor: Colors.PRIMARY,
  });

/* ================================================================== */
/* STYLES                                                             */
/* ================================================================== */

const Styles = () => (
  <style>{`
    .bk-page { font-family: ${T.font}; background: ${Colors.LIGHT_GRAY}; min-height: 100vh; padding: 24px 0 60px; }
    .bk-page .container-fluid { max-width: 1400px; }
    .bk-header { background: linear-gradient(135deg, ${Colors.GRADIENT_START} 0%, ${Colors.GRADIENT_END} 100%); color: #fff; border-radius: ${T.radius}px; padding: 26px 30px; margin-bottom: 20px; position: relative; overflow: hidden; box-shadow: 0 10px 30px rgba(14,107,248,.22); }
    .bk-header::after { content: ""; position: absolute; top: -60px; right: -60px; width: 220px; height: 220px; background: radial-gradient(circle, rgba(255,255,255,.18) 0%, transparent 70%); border-radius: 50%; }
    .bk-header h2 { font-weight: 700; letter-spacing: -.02em; margin: 0; font-size: 22px; }
    .bk-header p { opacity: .88; margin: 4px 0 0; font-size: 13.5px; }
    .bk-tabs { display: flex; gap: 6px; margin-bottom: 18px; flex-wrap: wrap; }
    .bk-tab { display: inline-flex; align-items: center; gap: 8px; padding: 9px 16px; border-radius: ${T.radiusSm}; border: 1px solid ${Colors.BORDER_GRAY}; background: #fff; color: ${Colors.TEXT_DARK}; font-weight: 500; font-size: 13.5px; cursor: pointer; transition: all .18s; }
    .bk-tab:hover:not(.active) { border-color: ${Colors.PRIMARY}; color: ${Colors.PRIMARY}; background: ${Colors.PRIMARY}0d; }
    .bk-tab.active { background: ${Colors.PRIMARY}; border-color: ${Colors.PRIMARY}; color: #fff; font-weight: 600; box-shadow: 0 4px 12px ${Colors.PRIMARY}33; }
    .bk-tab i { font-size: 15px; }
    .bk-card { background: #fff; border-radius: ${T.radius}px; border: ${T.border}; box-shadow: ${T.shadow}; overflow: hidden; }
    .bk-card-body { padding: 24px 26px; }
    .bk-section-title { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .08em; color: ${Colors.TEXT_LIGHT}; margin: 22px 0 10px; display: flex; align-items: center; gap: 8px; }
    .bk-section-title:first-of-type { margin-top: 0; }
    .bk-section-title i { color: ${Colors.PRIMARY}; }
    .bk-section-desc { font-size: 12.5px; color: ${Colors.TEXT_LIGHT}; margin: -4px 0 14px; line-height: 1.55; max-width: 720px; }
    .bk-form-row { padding-bottom: 16px; margin-bottom: 16px; border-bottom: 1px solid ${Colors.BORDER_GRAY}; }
    .bk-form-row:last-of-type { border-bottom: none; padding-bottom: 0; margin-bottom: 0; }
    .bk-form-row-label { font-size: 13px; font-weight: 600; color: ${Colors.TEXT_DARK}; margin-bottom: 4px; }
    .bk-form-row-help { font-size: 12px; color: ${Colors.TEXT_LIGHT}; margin: 0 0 10px; line-height: 1.5; }
    .bk-form-row-control { max-width: 480px; }
    .bk-input { border: 1.5px solid ${Colors.BORDER_GRAY} !important; background: #fff !important; border-radius: ${T.radiusSm} !important; padding: 9px 13px !important; font-size: 13.5px; color: ${Colors.TEXT_DARK} !important; transition: all .18s; width: 100%; height: auto !important; }
    .bk-input:hover { border-color: ${Colors.PRIMARY} !important; }
    .bk-input:focus { border-color: ${Colors.PRIMARY} !important; box-shadow: 0 0 0 3px ${Colors.PRIMARY}22 !important; outline: none; }
    .bk-input.is-invalid { border-color: ${ERROR_COLOR} !important; }
    .bk-input-error { font-size: 11.5px; color: ${ERROR_COLOR}; margin-top: 6px; }
    .bk-textarea { font-family: 'JetBrains Mono', 'Fira Code', ui-monospace, monospace !important; font-size: 12.5px !important; line-height: 1.6; min-height: 140px; resize: vertical; }
    .bk-btn-primary { background: ${Colors.PRIMARY} !important; border: none !important; color: #fff !important; border-radius: ${T.radiusSm} !important; font-weight: 600; padding: 9px 20px; font-size: 13.5px; transition: all .18s; min-width: 160px; display: inline-flex; align-items: center; justify-content: center; gap: 8px; }
    .bk-btn-primary:hover:not(:disabled) { background: ${Colors.PRIMARY} !important; filter: brightness(0.92); transform: translateY(-1px); box-shadow: 0 6px 16px ${Colors.PRIMARY}44; }
    .bk-btn-primary:disabled { background: #c8c8c8 !important; cursor: not-allowed; opacity: .7; }
    .bk-btn-ghost { background: transparent !important; border: 1px solid ${Colors.BORDER_GRAY} !important; color: ${Colors.TEXT_DARK} !important; border-radius: ${T.radiusSm} !important; font-weight: 500; padding: 8px 15px; font-size: 13px; transition: all .18s; display: inline-flex; align-items: center; gap: 6px; }
    .bk-btn-ghost:hover:not(:disabled) { border-color: ${Colors.PRIMARY} !important; color: ${Colors.PRIMARY} !important; background: ${Colors.PRIMARY}0d !important; }
    .bk-btn-ghost:disabled { opacity: .5 !important; cursor: not-allowed !important; }
    .bk-footer { margin-top: 24px; padding-top: 20px; border-top: ${T.border}; display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; }
    .bk-footer-actions { display: flex; gap: 10px; }
    .bk-footer-meta { font-size: 12px; color: ${Colors.TEXT_LIGHT}; }
    .bk-chip { display: inline-flex; align-items: center; gap: 6px; padding: 4px 10px; border-radius: 999px; font-size: 11.5px; font-weight: 600; background: ${Colors.LIGHT_GRAY}; color: ${TEXT_MEDIUM}; }
    .bk-chip-dot { width: 6px; height: 6px; border-radius: 50%; background: ${Colors.SUCCESS}; }
    .bk-chip-warn { background: ${Colors.WARNING}1a; color: #92400E; }
    .bk-chip-warn .bk-chip-dot { background: ${Colors.WARNING}; }
    .bk-info-box { display: flex; gap: 12px; padding: 13px 15px; border-radius: ${T.radiusSm}; background: ${Colors.PRIMARY}0a; border: 1px solid ${Colors.PRIMARY}22; margin-bottom: 16px; }
    .bk-info-box i { color: ${Colors.PRIMARY}; font-size: 18px; flex-shrink: 0; margin-top: 1px; }
    .bk-info-box p { margin: 0; font-size: 12.5px; color: ${Colors.TEXT_DARK}; line-height: 1.55; }
    .bk-info-box code { background: rgba(0,0,0,.05); padding: 1px 5px; border-radius: 4px; font-size: 11.5px; }
    .bk-warn-box { display: flex; gap: 12px; padding: 13px 15px; border-radius: ${T.radiusSm}; background: ${Colors.WARNING}14; border: 1px solid ${Colors.WARNING}33; margin-bottom: 16px; }
    .bk-warn-box i { color: ${Colors.WARNING}; font-size: 18px; flex-shrink: 0; margin-top: 1px; }
    .bk-warn-box p { margin: 0; font-size: 12.5px; color: ${Colors.TEXT_DARK}; line-height: 1.55; }
    .bk-skel { background: linear-gradient(90deg, ${Colors.LIGHT_GRAY} 25%, #eee 50%, ${Colors.LIGHT_GRAY} 75%); background-size: 200% 100%; animation: bk-shimmer 1.4s infinite; border-radius: 6px; }
    @keyframes bk-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
    .bk-tier-row { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px; margin-bottom: 16px; }
    @media (max-width: 720px) { .bk-tier-row { grid-template-columns: 1fr; } }
    .bk-tier { padding: 14px; border-radius: ${T.radiusSm}; border: 1px solid ${Colors.BORDER_GRAY}; background: #fff; }
    .bk-tier-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .08em; margin-bottom: 6px; }
    .bk-tier-value { font-size: 22px; font-weight: 700; color: ${Colors.TEXT_DARK}; margin-bottom: 4px; }
    .bk-tier-sub { font-size: 11.5px; color: ${Colors.TEXT_LIGHT}; line-height: 1.45; }
    .bk-tier.free { background: ${Colors.SUCCESS}0a; border-color: ${Colors.SUCCESS}33; }
    .bk-tier.free .bk-tier-label { color: ${Colors.SUCCESS}; }
    .bk-tier.half { background: ${Colors.WARNING}0a; border-color: ${Colors.WARNING}33; }
    .bk-tier.half .bk-tier-label { color: #92400E; }
    .bk-tier.full { background: ${ERROR_COLOR}0a; border-color: ${ERROR_COLOR}33; }
    .bk-tier.full .bk-tier-label { color: ${ERROR_COLOR}; }
  `}</style>
);

/* ================================================================== */
/* PRIMITIVE COMPONENTS                                               */
/* ================================================================== */

const TabHeader = ({ title, description }) => (
  <div style={{ marginBottom: 20 }}>
    <h3
      style={{
        margin: 0,
        color: Colors.TEXT_DARK,
        fontSize: 19,
        fontWeight: 700,
        letterSpacing: "-.01em",
      }}
    >
      {title}
    </h3>
    {description && (
      <p
        style={{
          margin: "6px 0 0",
          color: Colors.TEXT_LIGHT,
          fontSize: 13,
          maxWidth: 640,
          lineHeight: 1.5,
        }}
      >
        {description}
      </p>
    )}
  </div>
);

const SectionTitle = ({ icon, children }) => (
  <div className="bk-section-title">
    {icon && <i className={`bi ${icon}`} />}
    {children}
  </div>
);

const FormRow = ({ label, help, children }) => (
  <div className="bk-form-row">
    <div className="bk-form-row-label">{label}</div>
    {help && <p className="bk-form-row-help">{help}</p>}
    <div className="bk-form-row-control">{children}</div>
  </div>
);

const NumberInput = ({ value, onChange, step = "0.01", ...rest }) => (
  <input
    className="bk-input"
    type="number"
    step={step}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    {...rest}
  />
);

const InfoBox = ({ children }) => (
  <div className="bk-info-box">
    <i className="bi bi-info-circle" />
    <p>{children}</p>
  </div>
);

const WarnBox = ({ children }) => (
  <div className="bk-warn-box">
    <i className="bi bi-exclamation-triangle" />
    <p>{children}</p>
  </div>
);

const TabSkeleton = ({ rows = 5 }) => (
  <div>
    <div
      className="bk-skel"
      style={{ height: 20, width: "40%", marginBottom: 8 }}
    />
    <div
      className="bk-skel"
      style={{ height: 14, width: "70%", marginBottom: 28 }}
    />
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} style={{ marginBottom: 20 }}>
        <div
          className="bk-skel"
          style={{ height: 14, width: "30%", marginBottom: 10 }}
        />
        <div
          className="bk-skel"
          style={{ height: 42, width: "100%", maxWidth: 420 }}
        />
      </div>
    ))}
  </div>
);

const SaveFooter = ({ saving, onSave, onReset, hasChanges, label }) => (
  <div className="bk-footer">
    <div className="bk-footer-meta">
      {hasChanges === true ? (
        <span className="bk-chip bk-chip-warn">
          <span className="bk-chip-dot" /> Unsaved changes
        </span>
      ) : hasChanges === false ? (
        <span className="bk-chip">
          <span className="bk-chip-dot" /> All values saved
        </span>
      ) : (
        <span className="bk-chip">
          <span className="bk-chip-dot" /> Edits save to database
        </span>
      )}
    </div>
    <div className="bk-footer-actions">
      {onReset && (
        <Button
          className="bk-btn-ghost"
          onClick={onReset}
          disabled={saving || !hasChanges}
        >
          <i className="bi bi-arrow-counterclockwise" /> Reset
        </Button>
      )}
      <Button className="bk-btn-primary" onClick={onSave} disabled={saving}>
        {saving ? (
          <>
            <BSpinner as="span" animation="border" size="sm" /> Saving…
          </>
        ) : (
          <>
            <i className="bi bi-check2-circle" /> {label}
          </>
        )}
      </Button>
    </div>
  </div>
);

/* ================================================================== */
/* ROOT COMPONENT                                                     */
/* ================================================================== */

const Settings = () => {
  const [activeTab, setActiveTab] = useState(TABS[0].key);
  return (
    <div className="bk-page">
      <Styles />
      <Container fluid>
        <div className="bk-header">
          <div className="position-relative" style={{ zIndex: 1 }}>
            <h2>Settings</h2>
            <p>Manage fare rules, service limits, and cancellation policy</p>
          </div>
        </div>
        <div className="bk-tabs" role="tablist">
          {TABS.map(({ key, icon }) => {
            const isActive = activeTab === key;
            return (
              <button
                key={key}
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveTab(key)}
                className={`bk-tab ${isActive ? "active" : ""}`}
              >
                <i className={`bi ${icon}`} aria-hidden="true" />
                {key}
              </button>
            );
          })}
        </div>
        <div className="bk-card">
          <div className="bk-card-body">
            {activeTab === "Fare Configuration" && <FareConfigurationTab />}
            {activeTab === "Funeral Rates" && <FuneralRatesTab />}
            {activeTab === "Wedding Rates" && <WeddingRatesTab />}
            {activeTab === "School Run Rates" && <SchoolRunRatesTab />}
            {activeTab === "Service Limits" && <ServiceLimitsTab />}
            {activeTab === "Cancellation Policy" && <CancellationPolicyTab />}
          </div>
        </div>
      </Container>
    </div>
  );
};

/* ================================================================== */
/* TAB 1 — FARE CONFIGURATION                                         */
/* ================================================================== */

const FareConfigurationTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [charter, setCharter] = useState(SERVICE_CONFIGS.charter.empty);
  const [original, setOriginal] = useState(null);
  const [seasonalError, setSeasonalError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await fetchFareSettings();
        if (cancelled) return;
        const c = data.find((s) => s.service_type === "charter");
        if (c) {
          const cfg = c.service_config || {};
          const normalized = {
            base_fee: c.base_fee ?? "",
            minimum_fare: c.minimum_fare ?? "",
            diesel_price: c.diesel_price ?? "",
            fuel_efficiency_kmpl: c.fuel_efficiency_kmpl ?? "",
            fuel_buffer_percent: c.fuel_buffer_percent ?? "",
            driver_hourly_rate: c.driver_hourly_rate ?? "",
            driver_meal_allowance: cfg.driver_meal_allowance ?? 200,
            minimum_billable_hours: c.minimum_billable_hours ?? "",
            driver_daily_rate: c.driver_daily_rate ?? "",
            vehicle_cost_per_km: c.vehicle_cost_per_km ?? "",
            profit_margin_percent: c.profit_margin_percent ?? "",
            vehicle_standby_rate: cfg.vehicle_standby_rate ?? 1200,
            pickup_surcharge_free_km: cfg.pickup_surcharge_free_km ?? 5,
            pickup_surcharge_per_km: cfg.pickup_surcharge_per_km ?? 15,
            seasonal_multipliers: safeStringify(
              cfg.seasonal || c.seasonal_multipliers || {},
              2,
            ),
            road_condition:
              cfg.road_condition ||
              SERVICE_CONFIGS.charter.empty.road_condition,
          };
          setCharter(normalized);
          setOriginal(normalized);
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const updateCharter = useCallback((key, value) => {
    setCharter((prev) => ({ ...prev, [key]: value }));
    if (key === "seasonal_multipliers") validateSeasonal(value);
  }, []);

  const updateRoadCondition = useCallback((key, value) => {
    setCharter((prev) => ({
      ...prev,
      road_condition: { ...prev.road_condition, [key]: value },
    }));
  }, []);

  const validateSeasonal = (raw) => {
    if (!raw || raw.trim() === "" || raw.trim() === "{}") {
      setSeasonalError(null);
      return true;
    }
    try {
      const parsed = JSON.parse(raw);
      if (typeof parsed !== "object" || Array.isArray(parsed)) {
        setSeasonalError("Must be a JSON object, not an array.");
        return false;
      }
      const rangeRegex = /^\d{4}-\d{2}-\d{2}\.\.\d{4}-\d{2}-\d{2}$/;
      for (const [key, value] of Object.entries(parsed)) {
        if (key === "default") {
          if (typeof value !== "number" || value < 1 || value > 5) {
            setSeasonalError(
              "The 'default' value must be a number between 1.00 and 5.00.",
            );
            return false;
          }
          continue;
        }
        if (!rangeRegex.test(key)) {
          setSeasonalError(
            `Invalid range key "${key}". Use format YYYY-MM-DD..YYYY-MM-DD.`,
          );
          return false;
        }
        const [start, end] = key.split("..");
        if (start > end) {
          setSeasonalError(
            `Range "${key}" has a start date after its end date.`,
          );
          return false;
        }
        if (typeof value !== "number" || value < 1 || value > 5) {
          setSeasonalError(
            `The multiplier for "${key}" must be a number between 1.00 and 5.00.`,
          );
          return false;
        }
      }
      setSeasonalError(null);
      return true;
    } catch (e) {
      setSeasonalError("Invalid JSON: " + e.message);
      return false;
    }
  };

  const changedFields = useMemo(() => {
    if (!original) return [];
    return Object.keys(charter).filter((k) => {
      if (k === "road_condition")
        return (
          JSON.stringify(charter.road_condition) !==
          JSON.stringify(original.road_condition)
        );
      return String(charter[k]) !== String(original[k]);
    });
  }, [charter, original]);

  const anyChanges = changedFields.length > 0;

  const handleSave = async () => {
    if (!validateSeasonal(charter.seasonal_multipliers)) {
      showErrorAlert("Invalid seasonal format", seasonalError);
      return;
    }
    try {
      setSaving(true);
      const seasonalParsed =
        charter.seasonal_multipliers &&
        charter.seasonal_multipliers.trim() !== "{}"
          ? JSON.parse(charter.seasonal_multipliers)
          : null;
      await updateFareSetting("charter", {
        base_fee: parse(charter.base_fee),
        minimum_fare: parse(charter.minimum_fare),
        diesel_price: parse(charter.diesel_price),
        fuel_efficiency_kmpl: parse(charter.fuel_efficiency_kmpl),
        fuel_buffer_percent: parse(charter.fuel_buffer_percent),
        driver_hourly_rate: parse(charter.driver_hourly_rate),
        minimum_billable_hours: parse(charter.minimum_billable_hours),
        driver_daily_rate: parse(charter.driver_daily_rate),
        vehicle_cost_per_km: parse(charter.vehicle_cost_per_km),
        profit_margin_percent: parse(charter.profit_margin_percent),
        service_config: {
          driver_meal_allowance: parse(charter.driver_meal_allowance, 200),
          road_condition: charter.road_condition,
          seasonal: seasonalParsed,
          vehicle_standby_rate: parse(charter.vehicle_standby_rate, 1200),
          pickup_surcharge_free_km: parse(charter.pickup_surcharge_free_km, 5),
          pickup_surcharge_per_km: parse(charter.pickup_surcharge_per_km, 15),
        },
      });
      setOriginal(charter);
      showSuccessAlert(
        "Fare settings saved",
        "New quotes will use these values immediately.",
      );
    } catch (err) {
      setError(err.message);
      showErrorAlert("Save failed", err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (original) {
      setCharter(original);
      setSeasonalError(null);
    }
  };

  if (loading) return <TabSkeleton rows={10} />;

  return (
    <div>
      <TabHeader
        title="Fare Configuration"
        description="Charter pricing and road condition auto-detection. Charter is used for Transportation, Tour Service, and any service that isn't funeral, wedding, or school."
      />
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <SectionTitle icon="bi-fuel-pump">Fuel</SectionTitle>
      <FormRow
        label="Diesel Price (₱ / liter)"
        help="Update weekly from DOE Camarines Sur monitoring. Source: https://www.doe.gov.ph/price-monitoring"
      >
        <NumberInput
          value={charter.diesel_price}
          onChange={(v) => updateCharter("diesel_price", v)}
        />
      </FormRow>
      <FormRow
        label="Vehicle Fuel Efficiency (km / L)"
        help="Typical traditional jeepney: 6–8 km/L loaded. Modern: 8–10 km/L. E-jeep: n/a (electric)."
      >
        <NumberInput
          value={charter.fuel_efficiency_kmpl}
          onChange={(v) => updateCharter("fuel_efficiency_kmpl", v)}
        />
      </FormRow>
      <FormRow
        label="Fuel Buffer (%)"
        help="Covers traffic, hills, idling. Typical: 10%."
      >
        <NumberInput
          value={charter.fuel_buffer_percent}
          onChange={(v) => updateCharter("fuel_buffer_percent", v)}
        />
      </FormRow>

      <SectionTitle icon="bi-person-badge">Driver Compensation</SectionTitle>
      <FormRow
        label="Driver Hourly Rate (₱ / hour)"
        help="Source: Region V daily minimum wage (₱420) + meal allowance ÷ effective working hours ≈ ₱150/h."
      >
        <NumberInput
          value={charter.driver_hourly_rate}
          onChange={(v) => updateCharter("driver_hourly_rate", v)}
        />
      </FormRow>
      <FormRow
        label="Minimum Billable Hours"
        help="Standard PH workday. A trip shorter than this is still billed for this many hours."
      >
        <NumberInput
          value={charter.minimum_billable_hours}
          onChange={(v) => updateCharter("minimum_billable_hours", v)}
          step="0.5"
        />
      </FormRow>
      <FormRow
        label="Driver Daily Rate (₱ / day)"
        help="For each day beyond the first. Source: minimum wage + meals + profit share ≈ ₱800/day."
      >
        <NumberInput
          value={charter.driver_daily_rate}
          onChange={(v) => updateCharter("driver_daily_rate", v)}
        />
      </FormRow>

      {/* NEW: DRIVER MEAL ALLOWANCE INPUT */}
      <FormRow
        label="Driver Meal Allowance (₱ / day)"
        help="Cover breakfast, lunch, and snacks. Standard: ₱200/day."
      >
        <NumberInput
          value={charter.driver_meal_allowance}
          onChange={(v) => updateCharter("driver_meal_allowance", v)}
        />
      </FormRow>

      <SectionTitle icon="bi-truck">Vehicle & Base</SectionTitle>
      <FormRow
        label="Base Charter Fee (₱)"
        help="Fixed dispatch cost before any distance/usage. Covers paperwork, insurance overhead, and the operator's baseline time."
      >
        <NumberInput
          value={charter.base_fee}
          onChange={(v) => updateCharter("base_fee", v)}
        />
      </FormRow>
      <FormRow
        label="Vehicle Operating Cost (₱ / km)"
        help="Depreciation + maintenance + tires for a 10-year-old jeepney. Source: LTO + LTO-accredited repair shop estimates (2024)."
      >
        <NumberInput
          value={charter.vehicle_cost_per_km}
          onChange={(v) => updateCharter("vehicle_cost_per_km", v)}
        />
      </FormRow>

      <SectionTitle icon="bi-graph-up">Margins & Floors</SectionTitle>
      <FormRow
        label="Minimum Charter Fare (₱)"
        help="Floor that keeps a 3-hour charter profitable after driver + vehicle costs."
      >
        <NumberInput
          value={charter.minimum_fare}
          onChange={(v) => updateCharter("minimum_fare", v)}
        />
      </FormRow>
      <FormRow
        label="Profit / Service Margin (%)"
        help="Typical small-transport operator margin: 10–20%."
      >
        <NumberInput
          value={charter.profit_margin_percent}
          onChange={(v) => updateCharter("profit_margin_percent", v)}
        />
      </FormRow>
      <FormRow
        label="Vehicle Standby Rate (₱ / extra day)"
        help="Charged for each day beyond the first on multi-day charters. Represents the operator's lost daily earnings while the jeepney is reserved. Default: ₱1,200."
      >
        <NumberInput
          value={charter.vehicle_standby_rate}
          onChange={(v) => updateCharter("vehicle_standby_rate", v)}
          step="50"
        />
      </FormRow>
      <FormRow
        label="Pickup Surcharge — Free Distance (km)"
        help="Distance an operator can travel to reach the passenger at no extra charge. Beyond this, a per-km surcharge applies. Source: average intra-municipality deadhead distance — 5 km."
      >
        <NumberInput
          value={charter.pickup_surcharge_free_km}
          onChange={(v) => updateCharter("pickup_surcharge_free_km", v)}
          step="1"
        />
      </FormRow>
      <FormRow
        label="Pickup Surcharge — Rate (₱ / km)"
        help="Charged per km beyond the free distance. Covers the operator's deadhead drive to the pickup point. Source: fuel (₱89.40/L ÷ 7 km/L) + wear (₱10/km) × 1.10 buffer ≈ ₱15/km."
      >
        <NumberInput
          value={charter.pickup_surcharge_per_km}
          onChange={(v) => updateCharter("pickup_surcharge_per_km", v)}
          step="1"
        />
      </FormRow>

      <SectionTitle icon="bi-calendar-heart">Seasonal Multipliers</SectionTitle>
      <InfoBox>
        <strong>Applied to Charter bookings only.</strong>
        <br />
        Funeral, Wedding, and School Run rates are deliberately excluded —
        seasonal surcharges are inappropriate for those services.
        <br />
        <br />
        <strong>Format:</strong> JSON object. Each key is a date range{" "}
        <code>YYYY-MM-DD..YYYY-MM-DD</code>, each value is a multiplier between
        1.00 and 5.00. Add a <code>"default"</code> key to specify what applies
        outside those ranges.
        <br />
        <br />
        <strong>Example:</strong>
        <br />
        <code>{`{ "2025-12-20..2026-01-05": 1.30, "2026-03-25..2026-04-05": 1.25, "default": 1.00 }`}</code>
      </InfoBox>

      <FormRow label="Seasonal Multipliers (JSON)">
        <textarea
          className={`bk-input bk-textarea ${seasonalError ? "is-invalid" : ""}`}
          value={charter.seasonal_multipliers}
          onChange={(e) =>
            updateCharter("seasonal_multipliers", e.target.value)
          }
          spellCheck={false}
          placeholder={'{ "default": 1.00 }'}
        />
        {seasonalError && <div className="bk-input-error">{seasonalError}</div>}
      </FormRow>

      <SectionTitle icon="bi-signpost-split">
        Road Condition (Auto-Detection)
      </SectionTitle>
      <p className="bk-section-desc">
        The server detects road condition from the route. Passengers never
        choose it.
      </p>

      <FormRow
        label="Mountainous Municipalities"
        help="Any trip with pickup or destination in these municipalities is classified as Hilly. Comma-separated. Source: Rinconada geography — Buhi and Bato sit on the Malinao range."
      >
        <input
          className="bk-input"
          type="text"
          value={(charter.road_condition.mountainous_municipalities || []).join(
            ", ",
          )}
          onChange={(e) =>
            updateRoadCondition(
              "mountainous_municipalities",
              e.target.value
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean),
            )
          }
          placeholder="Buhi, Bato"
        />
      </FormRow>
      <FormRow
        label="Malinao Range Longitude"
        help="Any destination east of this longitude is classified as Hilly. Source: Mt. Malinao summit longitude ≈ 123.55°E."
      >
        <NumberInput
          value={charter.road_condition.malinao_range_longitude}
          onChange={(v) =>
            updateRoadCondition("malinao_range_longitude", parse(v))
          }
        />
      </FormRow>
      <FormRow label="Normal Multiplier (applied to fuel)">
        <NumberInput
          value={charter.road_condition.normal}
          onChange={(v) => updateRoadCondition("normal", parse(v))}
        />
      </FormRow>
      <FormRow
        label="Hilly Multiplier (applied to fuel)"
        help="Source: 20% fuel penalty for sustained incline, measured across Buhi/Bato routes."
      >
        <NumberInput
          value={charter.road_condition.hilly}
          onChange={(v) => updateRoadCondition("hilly", parse(v))}
        />
      </FormRow>
      <FormRow
        label="Difficult Multiplier (applied to fuel)"
        help="Source: 40% fuel penalty for unsealed/steep roads, measured across mountain barangays."
      >
        <NumberInput
          value={charter.road_condition.difficult}
          onChange={(v) => updateRoadCondition("difficult", parse(v))}
        />
      </FormRow>

      <SaveFooter
        saving={saving}
        onSave={handleSave}
        onReset={handleReset}
        hasChanges={anyChanges}
        label="Save Fare Settings"
      />
    </div>
  );
};

/* ================================================================== */
/* TAB 2 — FUNERAL RATES                                              */
/* ================================================================== */

const FuneralRatesTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [funeral, setFuneral] = useState(SERVICE_CONFIGS.funeral.empty);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchFareSettings();
        if (cancelled) return;
        const f = data.find((s) => s.service_type === "funeral");
        if (f) {
          const cfg = f.service_config || {};
          setFuneral({
            base_service: cfg.base_service ?? "",
            fuel_allowance: cfg.fuel_allowance ?? "",
            driver_wage: cfg.driver_wage ?? "",
            target: cfg.target ?? "",
          });
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const update = (k, v) => setFuneral((p) => ({ ...p, [k]: v }));

  const handleSave = async () => {
    try {
      setSaving(true);
      await updateFareSetting("funeral", {
        service_config: {
          base_service: parse(funeral.base_service),
          fuel_allowance: parse(funeral.fuel_allowance),
          driver_wage: parse(funeral.driver_wage),
          target: parse(funeral.target),
        },
      });
      showSuccessAlert("Funeral rates saved");
    } catch (err) {
      setError(err.message);
      showErrorAlert("Save failed", err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <TabSkeleton rows={5} />;

  return (
    <div>
      <TabHeader
        title="Funeral Rates"
        description="Flat rate for burial processions. Municipality-locked — passengers can only book funerals with operators based in their municipality."
      />
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}
      <InfoBox>
        The margin is computed as <code>target − (base + fuel + driver)</code>.
        All components are shown on the passenger's pricing card.
        <br />
        <br />
        <strong>Seasonal multipliers do not apply here.</strong>
      </InfoBox>
      <SectionTitle icon="bi-cash-coin">Components</SectionTitle>
      <FormRow
        label="Base Service Fee (₱)"
        help="Source: cleaning + decoration + procession prep labor at minimum wage × 4 hours ≈ ₱700."
      >
        <NumberInput
          value={funeral.base_service}
          onChange={(v) => update("base_service", v)}
        />
      </FormRow>
      <FormRow
        label="Fuel Allowance (₱)"
        help="Source: 20 km procession × 7 km/L × ₱89.40/L + buffer ≈ ₱300."
      >
        <NumberInput
          value={funeral.fuel_allowance}
          onChange={(v) => update("fuel_allowance", v)}
        />
      </FormRow>
      <FormRow
        label="Driver Wage (₱)"
        help="Source: half-day wage (₱420 minimum × 0.5 rounded) ≈ ₱400."
      >
        <NumberInput
          value={funeral.driver_wage}
          onChange={(v) => update("driver_wage", v)}
        />
      </FormRow>
      <SectionTitle icon="bi-flag">Target</SectionTitle>
      <FormRow
        label="Target Rate (₱)"
        help="Final fare the passenger pays. Source: lowest funeral-charter rate quoted in Rinconada (market survey). Margin = target − sum of components."
      >
        <NumberInput
          value={funeral.target}
          onChange={(v) => update("target", v)}
        />
      </FormRow>
      <SaveFooter
        saving={saving}
        onSave={handleSave}
        label="Save Funeral Rates"
      />
    </div>
  );
};

/* ================================================================== */
/* TAB 3 — WEDDING RATES                                              */
/* ================================================================== */

const WeddingRatesTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [wedding, setWedding] = useState(SERVICE_CONFIGS.wedding.empty);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchFareSettings();
        if (cancelled) return;
        const w = data.find((s) => s.service_type === "wedding");
        if (w) {
          const zones = w.service_config?.zones || {};
          const normalized = { zones: {} };
          [1, 2, 3].forEach((z) => {
            normalized.zones[z] = {
              base: zones[z]?.base ?? "",
              fuel: zones[z]?.fuel ?? "",
              driver: zones[z]?.driver ?? "",
              reservation: zones[z]?.reservation ?? "",
              target: zones[z]?.target ?? "",
            };
          });
          setWedding(normalized);
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const updateZone = (zone, field, value) => {
    setWedding((prev) => ({
      zones: { ...prev.zones, [zone]: { ...prev.zones[zone], [field]: value } },
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const zones = {};
      [1, 2, 3].forEach((z) => {
        zones[z] = {
          base: parse(wedding.zones[z].base),
          fuel: parse(wedding.zones[z].fuel),
          driver: parse(wedding.zones[z].driver),
          reservation: parse(wedding.zones[z].reservation),
          target: parse(wedding.zones[z].target),
        };
      });
      await updateFareSetting("wedding", { service_config: { zones } });
      showSuccessAlert("Wedding rates saved");
    } catch (err) {
      setError(err.message);
      showErrorAlert("Save failed", err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <TabSkeleton rows={12} />;

  return (
    <div>
      <TabHeader
        title="Wedding Rates"
        description="Zone-based pricing. Zone is determined by comparing the pickup and destination municipalities to the operator's municipality."
      />
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}
      <InfoBox>
        <strong>Seasonal multipliers do not apply to weddings.</strong> The rate
        is flat per zone regardless of date.
        <br />
        <br />
        Source for all three zones: market survey of Rinconada wedding-charter
        quotes, Feb 2025.
      </InfoBox>
      {[1, 2, 3].map((zone) => (
        <div key={zone}>
          <SectionTitle icon="bi-calendar-event">
            {WEDDING_ZONE_LABELS[zone]}
          </SectionTitle>
          <FormRow
            label="Base Service Fee (₱)"
            help={
              zone === 1
                ? "Covers paperwork + procession prep."
                : zone === 2
                  ? "Adds inter-municipality permit costs."
                  : "Adds inter-province permit + travel time."
            }
          >
            <NumberInput
              value={wedding.zones[zone].base}
              onChange={(v) => updateZone(zone, "base", v)}
            />
          </FormRow>
          <FormRow
            label="Fuel Allowance (₱)"
            help={
              zone === 1
                ? "≈ 20 km round trip × ₱89.40/L ÷ 7 km/L × 1.10 buffer."
                : zone === 2
                  ? "≈ 40 km round trip."
                  : "≈ 80+ km round trip."
            }
          >
            <NumberInput
              value={wedding.zones[zone].fuel}
              onChange={(v) => updateZone(zone, "fuel", v)}
            />
          </FormRow>
          <FormRow
            label="Driver Wage (₱)"
            help="Full-day wage — weddings run 6–10 hours including wait time."
          >
            <NumberInput
              value={wedding.zones[zone].driver}
              onChange={(v) => updateZone(zone, "driver", v)}
            />
          </FormRow>
          <FormRow
            label="Vehicle Reservation (₱)"
            help="The jeepney is unavailable to other passengers for the whole day. This offsets the lost bookings."
          >
            <NumberInput
              value={wedding.zones[zone].reservation}
              onChange={(v) => updateZone(zone, "reservation", v)}
            />
          </FormRow>
          <FormRow
            label="Target Rate (₱)"
            help="Final fare the passenger pays. Margin = target − sum of components."
          >
            <NumberInput
              value={wedding.zones[zone].target}
              onChange={(v) => updateZone(zone, "target", v)}
            />
          </FormRow>
        </div>
      ))}
      <SaveFooter
        saving={saving}
        onSave={handleSave}
        label="Save Wedding Rates"
      />
    </div>
  );
};

/* ================================================================== */
/* TAB 4 — SCHOOL RUN RATES                                           */
/* ================================================================== */

const SchoolRunRatesTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [school, setSchool] = useState(SERVICE_CONFIGS.school_run.empty);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchFareSettings();
        if (cancelled) return;
        const s = data.find((x) => x.service_type === "school_run");
        if (s) {
          const cfg = s.service_config || {};
          const load = (z = {}) => ({
            base: z.base ?? "",
            driver: z.driver ?? "",
            fuel: z.fuel ?? "",
            reservation: z.reservation ?? "",
            target: z.target ?? "",
          });
          setSchool({
            in_district: load(cfg.in_district),
            out_district: load(cfg.out_district),
            overage_threshold_km: cfg.overage_threshold_km ?? "",
            overage_per_km: cfg.overage_per_km ?? "",
          });
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const updateZone = (zone, field, value) =>
    setSchool((prev) => ({
      ...prev,
      [zone]: { ...prev[zone], [field]: value },
    }));
  const updateTop = (field, value) =>
    setSchool((prev) => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    try {
      setSaving(true);
      const service_config = {
        in_district: {
          base: parse(school.in_district.base),
          driver: parse(school.in_district.driver),
          fuel: parse(school.in_district.fuel),
          reservation: parse(school.in_district.reservation),
          target: parse(school.in_district.target),
        },
        out_district: {
          base: parse(school.out_district.base),
          driver: parse(school.out_district.driver),
          fuel: parse(school.out_district.fuel),
          reservation: parse(school.out_district.reservation),
          target: parse(school.out_district.target),
        },
        overage_threshold_km: parse(school.overage_threshold_km, 60),
        overage_per_km: parse(school.overage_per_km, 15),
      };
      await updateFareSetting("school_run", { service_config });
      showSuccessAlert("School Run rates saved");
    } catch (err) {
      setError(err.message);
      showErrorAlert("Save failed", err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <TabSkeleton rows={12} />;

  const renderZone = (key, label, helper) => (
    <>
      <SectionTitle icon="bi-geo">{label}</SectionTitle>
      <p className="bk-section-desc">{helper}</p>
      <FormRow label="Base Dispatch Fee (₱)">
        <NumberInput
          value={school[key].base}
          onChange={(v) => updateZone(key, "base", v)}
        />
      </FormRow>
      <FormRow label="Driver Daily Wage (₱)">
        <NumberInput
          value={school[key].driver}
          onChange={(v) => updateZone(key, "driver", v)}
        />
      </FormRow>
      <FormRow label="Fuel Allowance (₱)">
        <NumberInput
          value={school[key].fuel}
          onChange={(v) => updateZone(key, "fuel", v)}
        />
      </FormRow>
      <FormRow label="Vehicle Reservation (₱)">
        <NumberInput
          value={school[key].reservation}
          onChange={(v) => updateZone(key, "reservation", v)}
        />
      </FormRow>
      <FormRow label="Target Rate (₱)">
        <NumberInput
          value={school[key].target}
          onChange={(v) => updateZone(key, "target", v)}
        />
      </FormRow>
    </>
  );

  return (
    <div>
      <TabHeader
        title="School Run Rates"
        description="Daily rate for school bookings. Distinguishes trips within Rinconada from trips outside. Extra distance beyond the threshold adds a per-km overage."
      />
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}
      <InfoBox>
        <strong>School runs are flat daily rates.</strong> Seasonal multipliers
        do not apply — parents contract these per school year, not per peak
        week.
        <br />
        <br />
        Source for both zones: market survey of Rinconada school-service
        contracts, academic year 2024–2025.
      </InfoBox>
      {renderZone(
        "in_district",
        "Within Rinconada",
        "Both pickup and destination inside the 7 Rinconada municipalities.",
      )}
      {renderZone(
        "out_district",
        "Outside Rinconada",
        "Pickup or destination outside the 7 municipalities (e.g. Naga, Pili, Legazpi).",
      )}
      <SectionTitle icon="bi-rulers">Distance Overage</SectionTitle>
      <p className="bk-section-desc">
        Applies to the round-trip distance. Short trips skip this entirely.
      </p>
      <FormRow
        label="Overage Threshold (km)"
        help="Distance included in the daily rate. Source: average daily round-trip within Rinconada school routes — 60 km."
      >
        <NumberInput
          value={school.overage_threshold_km}
          onChange={(v) => updateTop("overage_threshold_km", v)}
          step="1"
        />
      </FormRow>
      <FormRow
        label="Overage Rate (₱ / km)"
        help="Source: same as pickup surcharge — consistent marginal cost per extra km."
      >
        <NumberInput
          value={school.overage_per_km}
          onChange={(v) => updateTop("overage_per_km", v)}
        />
      </FormRow>
      <SaveFooter
        saving={saving}
        onSave={handleSave}
        label="Save School Run Rates"
      />
    </div>
  );
};

/* ================================================================== */
/* TAB 5 — SERVICE LIMITS                                             */
/* ================================================================== */

const ServiceLimitsTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [limits, setLimits] = useState({
    max_serviceable_km: 500,
    max_multi_day: 7,
    fare_requote_tolerance_pct: 5,
  });
  const [original, setOriginal] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await fetchFareSettings();
        if (cancelled) return;
        const charter = data.find((s) => s.service_type === "charter");
        const serverLimits = charter?.service_config?.__limits || {};
        const values = {
          max_serviceable_km: serverLimits.max_serviceable_km ?? 500,
          max_multi_day: serverLimits.max_multi_day ?? 7,
          fare_requote_tolerance_pct:
            (serverLimits.fare_requote_tolerance ?? 0.05) * 100,
        };
        setLimits(values);
        setOriginal(values);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      await updateFareSetting("charter", {
        service_config: {
          __limits: {
            max_serviceable_km: parse(limits.max_serviceable_km, 500),
            max_multi_day: parse(limits.max_multi_day, 7),
            fare_requote_tolerance:
              parse(limits.fare_requote_tolerance_pct, 5) / 100,
          },
        },
      });
      setOriginal(limits);
      showSuccessAlert(
        "Limits saved",
        "New bookings will use these limits immediately.",
      );
    } catch (err) {
      setError(err.message);
      showErrorAlert("Save failed", err.message);
    } finally {
      setSaving(false);
    }
  };

  const changedFields = useMemo(() => {
    if (!original) return [];
    return Object.keys(limits).filter(
      (k) => String(limits[k]) !== String(original[k]),
    );
  }, [limits, original]);

  if (loading) return <TabSkeleton rows={4} />;

  return (
    <div>
      <TabHeader
        title="Service Limits"
        description="The hard caps that gate every booking. These protect both passengers (no surprise absurd fares) and operators (no wasted dispatches)."
      />
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}
      <InfoBox>
        <strong>These limits are live.</strong> Saving here updates{" "}
        <code>fare_settings.service_config.__limits</code> on the charter row,
        which <code>RentalController::serviceLimit()</code> reads on every
        create, update, and fare quote. Changes apply on the very next request.
        <br />
        <br />
        <strong>Fallback:</strong> if the charter row is ever missing or
        inactive, the server falls back to <code>config/arkila.php</code> (
        <code>MAX_SERVICEABLE_KM</code>, <code>MAX_MULTI_DAY</code>,{" "}
        <code>FARE_REQUOTE_TOLERANCE</code> in <code>.env</code>). Keep those
        env values aligned with what you set here as a safety net.
      </InfoBox>
      <SectionTitle icon="bi-signpost">Distance &amp; Duration</SectionTitle>
      <FormRow
        label="Maximum Serviceable Distance (km, one-way)"
        help="Source: the longest practical day-trip an operator in Rinconada can service and return within the 7-day cap. Manila round trip ≈ 760 km; we cap one-way legs at 500 km so a round trip fits inside the multi-day window. Rejects with DISTANCE_OUT_OF_RANGE."
      >
        <NumberInput
          value={limits.max_serviceable_km}
          onChange={(v) => setLimits((p) => ({ ...p, max_serviceable_km: v }))}
          step="10"
        />
      </FormRow>
      <FormRow
        label="Maximum Booking Duration (days)"
        help="Source: maximum reasonable charter for a family event; beyond this, per-day standby cost exceeds a fresh booking. Rejects with DURATION_EXCEEDED."
      >
        <NumberInput
          value={limits.max_multi_day}
          onChange={(v) => setLimits((p) => ({ ...p, max_multi_day: v }))}
          step="1"
        />
      </FormRow>
      <SectionTitle icon="bi-shield-lock">Anti-Tamper</SectionTitle>
      <FormRow
        label="Fare Re-quote Tolerance (%)"
        help="If the server-computed fare differs from the client-supplied fare by more than this percentage, the booking is rejected with FARE_CHANGED so the passenger re-confirms the new total. Source: 5% — wide enough for rounding, narrow enough to catch manipulation."
      >
        <NumberInput
          value={limits.fare_requote_tolerance_pct}
          onChange={(v) =>
            setLimits((p) => ({ ...p, fare_requote_tolerance_pct: v }))
          }
          step="0.5"
        />
      </FormRow>
      <InfoBox>
        <strong>Why these caps exist:</strong>
        <ul style={{ margin: "8px 0 0 18px", paddingLeft: 0 }}>
          <li>
            <strong>500 km</strong> — anything longer isn't a "charter" anymore;
            it's interstate transport, which the platform doesn't insure.
          </li>
          <li>
            <strong>7 days</strong> — beyond a week, the operator loses more
            from missed rebookings than they earn from the extended charter.
          </li>
          <li>
            <strong>5% fare tolerance</strong> — prevents a malicious client
            from submitting a ₱1 fare on a ₱5,000 trip.
          </li>
        </ul>
      </InfoBox>
      <SaveFooter
        saving={saving}
        onSave={handleSave}
        onReset={() => original && setLimits(original)}
        hasChanges={changedFields.length > 0}
        label="Save Service Limits"
      />
    </div>
  );
};

/* ================================================================== */
/* TAB 6 — CANCELLATION POLICY (UPDATED FOR 24H / 50% RULE)           */
/* ================================================================== */

const CancellationPolicyTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [serviceType, setServiceType] = useState("charter");

  // Simplified 24h / 50% State
  const [policy, setPolicy] = useState({
    free_hours: 24,
    half_rate_pct: 50,
  });
  const [original, setOriginal] = useState(null);

  const SERVICE_OPTIONS = [
    { value: "charter", label: "Charter / Transportation" },
    { value: "wedding", label: "Wedding" },
    { value: "funeral", label: "Funeral (Grief Exception)" },
    { value: "school_run", label: "School Run" },
  ];

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await fetchFareSettings();
        if (cancelled) return;

        const targetService = data.find((s) => s.service_type === serviceType);
        const serverPolicy =
          targetService?.service_config?.__cancellation_policy || {};

        const values = {
          free_hours: serverPolicy.free_hours ?? 24,
          half_rate_pct: (serverPolicy.half_rate ?? 0.5) * 100,
        };
        setPolicy(values);
        setOriginal(values);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [serviceType]);

  const handleSave = async () => {
    try {
      setSaving(true);

      // We explicitly overwrite the old 3-tier config with the new 2-tier config
      await updateFareSetting(serviceType, {
        service_config: {
          __cancellation_policy: {
            free_hours: parse(policy.free_hours, 24),
            half_rate: parse(policy.half_rate_pct, 50) / 100,
          },
        },
      });
      setOriginal(policy);
      showSuccessAlert(
        "Cancellation policy saved",
        `Updated policy for ${serviceType}.`,
      );
    } catch (err) {
      setError(err.message);
      showErrorAlert("Save failed", err.message);
    } finally {
      setSaving(false);
    }
  };

  const changedFields = useMemo(() => {
    if (!original) return [];
    return Object.keys(policy).filter(
      (k) => String(policy[k]) !== String(original[k]),
    );
  }, [policy, original]);

  if (loading) return <TabSkeleton rows={4} />;

  return (
    <div>
      <TabHeader
        title="Cancellation Policy"
        description="Define how much is retained when a passenger cancels, based on the service type."
      />
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <FormRow label="Select Service Type">
        <select
          className="bk-input"
          value={serviceType}
          onChange={(e) => setServiceType(e.target.value)}
        >
          {SERVICE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </FormRow>

      <InfoBox>
        <strong>
          Editing policy for:{" "}
          {SERVICE_OPTIONS.find((o) => o.value === serviceType)?.label}
        </strong>
      </InfoBox>

      <SectionTitle icon="bi-clock-history">Time Threshold</SectionTitle>
      <FormRow
        label="Free Cancellation Window (hours before pickup)"
        help="Cancellations made more than this many hours before pickup are free (0% fee). Cancellations made inside this window are charged the late fee."
      >
        <NumberInput
          value={policy.free_hours}
          onChange={(v) => setPolicy((p) => ({ ...p, free_hours: v }))}
          step="1"
        />
      </FormRow>

      <SectionTitle icon="bi-percent">Fee Rates</SectionTitle>
      <FormRow
        label="Late Cancellation Penalty Rate (% of fare)"
        help="Charged when cancelled within the free window (e.g. less than 24 hours before pickup). Typical: 50%."
      >
        <NumberInput
          value={policy.half_rate_pct}
          onChange={(v) => setPolicy((p) => ({ ...p, half_rate_pct: v }))}
          step="5"
        />
      </FormRow>

      <SectionTitle icon="bi-bar-chart">Preview</SectionTitle>
      <div className="bk-tier-row" style={{ gridTemplateColumns: "1fr 1fr" }}>
        <div className="bk-tier free">
          <div className="bk-tier-label">Free Cancellation</div>
          <div className="bk-tier-value">0%</div>
          <div className="bk-tier-sub">
            More than <strong>{policy.free_hours}h</strong> before pickup
          </div>
        </div>
        <div className="bk-tier half">
          <div className="bk-tier-label">Late Penalty</div>
          <div className="bk-tier-value">{policy.half_rate_pct}%</div>
          <div className="bk-tier-sub">
            Within <strong>{policy.free_hours}h</strong> of pickup
          </div>
        </div>
      </div>

      <InfoBox>
        <strong>How the fee is applied:</strong>
        <br />
        The retained amount is written to <code>
          rentals.cancellation_fee
        </code>{" "}
        and must be paid via GCash before the booking is officially marked as
        cancelled.
      </InfoBox>

      <SaveFooter
        saving={saving}
        onSave={handleSave}
        onReset={() => original && setPolicy(original)}
        hasChanges={changedFields.length > 0}
        label={`Save ${serviceType} Policy`}
      />
    </div>
  );
};

export default Settings;
